import { createFileRoute, Link } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, candidateDetails, CandidateDetail, Candidate } from '@/lib/mocks'
import { useCandidate, advanceCandidateStage, stageLabel } from '@/lib/candidateStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/states'
import { PageHeader, MatchScore, Avatar, SourceTag } from '@/lib/atsUi'
import { Check, AlertTriangle, Briefcase, GraduationCap, Wrench, MapPin, Clock, Mail, Phone, Building2, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/candidates/$candidateId')({
  component: CandidateDetailScreen,
})

function buildFallback(base: Candidate): CandidateDetail {
  const topSkills = base.skills.slice(0, 3)
  const currentCtcNum = 10 + base.totalYears * 2
  const expectedCtcNum = Math.round(currentCtcNum * 1.25)

  return {
    ...base,
    email: `${base.name.split(' ')[0].toLowerCase()}@email.com`,
    phone: '+91 98xxxx0000',
    noticePeriod: '30 days',
    currentCtc: `₹${currentCtcNum} LPA`,
    expectedCtc: `₹${expectedCtcNum} LPA`,
    experience: [
      {
        title: base.title,
        company: base.currentCompany,
        period: 'Present',
        summary: `Working as ${base.title} at ${base.currentCompany}, contributing across core product areas with expertise in ${base.skills.slice(0, 2).join(' and ')}.`,
      },
    ],
    education: [
      { degree: 'B.Tech, Computer Science', institution: 'NIT / IIT / Premier Institute', year: `${2024 - base.totalYears}` },
    ],
    matchRationale: {
      score: base.matchScore,
      matched: topSkills.map((skill) => ({
        skill,
        evidence: `Demonstrated ${skill} experience through role at ${base.currentCompany}`,
      })),
      gaps: [],
      summary: `${base.totalYears} years of relevant experience with strong alignment to the role requirements. Skills in ${base.skills.join(', ')} match the job description well. Located in ${base.location}.`,
    },
  }
}

function FactItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  )
}

function CandidateDetailScreen() {
  const { candidateId } = Route.useParams()
  const base = useCandidate(candidateId)

  const detail: CandidateDetail | null =
    candidateDetails[candidateId] ?? (base ? buildFallback(base) : null)

  if (!detail) {
    return (
      <DashboardLayout title="Candidate" navSections={atsNav} tenant={currentTenant} user={currentUser}>
        <EmptyState title="Candidate not found" description="This candidate does not exist or may have been removed." />
      </DashboardLayout>
    )
  }

  const { matchRationale } = detail

  function handleAdvance() {
    const next = advanceCandidateStage(candidateId)
    if (next) toast.success(`${detail!.name} moved to ${stageLabel(next)}`)
    else toast(`${detail!.name} is already at the final stage`)
  }

  return (
    <DashboardLayout title={detail.name} navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="space-y-8">
        {/* Back link */}
        <Link
          to="/candidates"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Candidates
        </Link>

        <PageHeader
          title={detail.name}
          subtitle="Full profile and the AI's reasoning for the match."
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleAdvance}>
                Advance to next stage
              </Button>
              <Button size="sm" variant="outline" onClick={() => toast.success('Client portal link copied — ready to send to the client')}>
                Share with client
              </Button>
            </div>
          }
        />

        {/* Header card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Avatar name={detail.name} />
              </div>
              <div className="space-y-1.5 min-w-0">
                <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>{detail.title}</span>
                  <span className="text-border">·</span>
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" />
                    {detail.currentCompany}
                  </span>
                  <span className="text-border">·</span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {detail.location}
                  </span>
                </p>
                <div className="flex items-center gap-3 pt-0.5">
                  <MatchScore score={detail.matchScore} />
                  <SourceTag source={detail.source} />
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t pt-5">
              <FactItem icon={<span className="text-xs">₹</span>} label="Current CTC" value={detail.currentCtc} />
              <FactItem icon={<span className="text-xs">₹</span>} label="Expected CTC" value={detail.expectedCtc} />
              <FactItem icon={<Clock className="h-3.5 w-3.5" />} label="Notice" value={detail.noticePeriod} />
              <FactItem icon={<Briefcase className="h-3.5 w-3.5" />} label="Experience" value={`${detail.totalYears} yrs`} />
              <FactItem icon={<Mail className="h-3.5 w-3.5" />} label="Email" value={detail.email} />
              <FactItem icon={<Phone className="h-3.5 w-3.5" />} label="Phone" value={detail.phone} />
            </div>
          </CardContent>
        </Card>

        {/* Body: 2-col grid */}
        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* LEFT: Experience, Education, Skills */}
          <div className="space-y-6">
            {/* Experience */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5" />
                Experience
              </h2>
              <Card>
                <CardContent className="pt-5">
                  <ol className="space-y-6">
                    {detail.experience.map((exp, i) => (
                      <li key={i} className="relative pl-5">
                        <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" />
                        {i < detail.experience.length - 1 && (
                          <span className="absolute left-[3px] top-3.5 h-full w-px bg-border" />
                        )}
                        <p className="font-medium text-foreground text-sm">{exp.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {exp.company}
                          <span className="mx-1.5">·</span>
                          {exp.period}
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{exp.summary}</p>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>

            {/* Education */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5" />
                Education
              </h2>
              <Card>
                <CardContent className="pt-5">
                  <ul className="divide-y divide-border">
                    {detail.education.map((edu, i) => (
                      <li key={i} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                        <div>
                          <p className="text-sm font-medium text-foreground">{edu.degree}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{edu.institution}</p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{edu.year}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Skills */}
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                <Wrench className="h-3.5 w-3.5" />
                Skills
              </h2>
              <Card>
                <CardContent className="pt-5">
                  <div className="flex flex-wrap gap-2">
                    {detail.skills.map((skill) => (
                      <span
                        key={skill}
                        className="rounded bg-muted px-2.5 py-1 text-xs font-medium text-foreground"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* RIGHT: Why matched */}
          <div>
            <h2 className="text-sm font-medium text-muted-foreground mb-3">Why matched</h2>
            <Card className="border-primary/30 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <CardTitle className="text-base font-semibold text-foreground">AI match reasoning</CardTitle>
                  <div className="flex flex-col items-end">
                    <span className="text-3xl font-bold text-primary leading-none">{matchRationale.score}</span>
                    <span className="text-xs text-muted-foreground mt-0.5">match score</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-3">
                  {matchRationale.summary}
                </p>

                {/* Matched skills */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Matched</p>
                  <ul className="space-y-3">
                    {matchRationale.matched.map((m, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex-shrink-0 rounded-full bg-success/15 p-0.5">
                          <Check className="h-3 w-3 text-success" />
                        </span>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-foreground">{m.skill}</span>
                          <span className="text-muted-foreground"> — </span>
                          <span className="text-sm text-muted-foreground">{m.evidence}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Gaps */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Gaps</p>
                  {matchRationale.gaps.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No notable gaps.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {matchRationale.gaps.map((gap, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <span className="mt-0.5 flex-shrink-0 rounded-full bg-warning/20 p-0.5">
                            <AlertTriangle className="h-3 w-3 text-warning-foreground" />
                          </span>
                          <span className="text-sm text-muted-foreground">{gap}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
